import { useTranslation } from 'react-i18next';
import { useShepherdTour } from 'react-shepherd';
import { useSelector } from 'react-redux';
import { selectProfileData } from '@pages/profile/store/selectors';
import useWalletsMixed from '@main-hooks/useWalletsMix';
import useMobileAgent from '@main-hooks/useMobileAgent';
import { useRef } from 'react';
import useUserPreferences from '@main-hooks/useUserPreferences';
import { UserPreferencesKeys } from '@main-hooks/useUserPreferences/useUserPreferences.types';
import { tourOptions, steps } from '../tours/CreateWalletCard.tour';
import { walletsThatCanBeCreated } from '../components/CreateWalletCard/utils';

const useAddWalletTour = (): { startTour: VoidFunction } => {
  const { walletsDataMixed } = useWalletsMixed();
  const [isMobileDevice] = useMobileAgent();
  const { updateOrCreateUserPreference, preferences } = useUserPreferences();
  const { t, i18n } = useTranslation();

  const userProfile = useSelector(selectProfileData);
  const { isAwActive } = userProfile || {};

  const tour = useShepherdTour({
    tourOptions,
    steps: steps(t, isMobileDevice, i18n.language as 'es' | 'en'),
  });

  const isTourActive = useRef(false);

  const onTourCompleted = () => {
    updateOrCreateUserPreference(UserPreferencesKeys.TUTORIAL_ADD_WALLET, true);
  };

  tour?.on('cancel', onTourCompleted);

  const startTour = () => {
    const isTutorialToAddWalletComplete = preferences.find(
      (preference) => preference.key === UserPreferencesKeys.TUTORIAL_ADD_WALLET
    )?.value;
    const isPlatformTutorialComplete = preferences.find(
      (preference) => preference.key === UserPreferencesKeys.TUTORIAL_COMPLETED
    )?.value;
    const listOfPendingWalletsToAdd = walletsThatCanBeCreated(
      walletsDataMixed?.map((wallet) => wallet.currency.name) || []
    );

    if (isTutorialToAddWalletComplete) return;
    if (!isPlatformTutorialComplete) return;
    if (isTourActive.current) return;
    if (!listOfPendingWalletsToAdd.length) return;
    if (!isAwActive) return;
    if (!tour) return;

    tour.start();
    isTourActive.current = true;
  };

  return { startTour };
};

export default useAddWalletTour;
